import { PiFilePdfDuotone } from 'react-icons/pi';
import { TmdxAvatar, TmdxChip } from '@ds';
import { LinkToCase } from '@ui';
import { useMemo } from 'react';
import { getIdentityAccessLogRecord } from '@features/components/ClinicalData/components/ClinicalLog/LogItems/utils';
import {
  Case,
  CaseLog,
  CaseLogEventType,
  CaseLogRecord,
  CaseResourceType,
  CaseSalesOrderStatus,
  CaseStatus,
  CaseUserGroupNames,
  EmployeeLabels,
  OrganOnOCSDeclineReason,
  OutcomeType,
  RandomizationResultType,
  ResourceLogisticsStatus,
  RoutePlanStatus,
} from '@/api/apis/case';
import { formatCurrency, getCaseStatusName, getUnosId, yesNo } from '@/utils/helpers';
import {
  getNoteByCreatedAt,
  renderDetailsRecordField,
  renderDonorDataUpdatedRecordField,
  renderFromTo,
  renderResource,
} from './utils';
import {
  caseResourceTypeNames,
  caseSalesOrderStatusNames,
  outcomeTypeNames,
  randomizationTypeNames,
  routeStatusNames,
} from '@/constants/case/case.constants';
import {
  employeeLabelLogEventName,
  RESOURCE_STATUS_TEXT_MAP,
  resourceLogisticsStatusNames,
} from '@/constants/resources.constants';
import { ORGAN_DECLINED_TEXTS } from './constants';

type CaseLogRecordContentProps = {
  log: CaseLog;
  record: CaseLogRecord;
  caseData: Case;
};

export const CaseLogCardContentRecord = ({ record, log, caseData }: CaseLogRecordContentProps) => {
  const {
    donorId,
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    notes,
    isExpeditedCase,
    randomization,
    laterality,
    duplicateCaseNumber,
  } = caseData;
  const unosId = getUnosId(
    donorId,
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );
  const matchingNote = useMemo(() => getNoteByCreatedAt(notes, log), [notes, log]);
  const showRecordFields = !!log.logRecords[0]?.propertyName || log.logRecords.length > 1;

  switch (log.eventType) {
    case CaseLogEventType.CaseDraftCreated:
    case CaseLogEventType.CaseCreated:
      return (
        <TmdxChip size="sm" color="info">
          {unosId}
        </TmdxChip>
      );
    case CaseLogEventType.CaseTxCenterDraftCreated:
    case CaseLogEventType.CaseClinicalScreeningCreated:
      return showRecordFields ? (
        renderDetailsRecordField(record)
      ) : (
        <TmdxChip size="sm" color="info">
          {unosId}
        </TmdxChip>
      );
    case CaseLogEventType.CaseNoteAdded:
    case CaseLogEventType.CaseNoteUpdated:
    case CaseLogEventType.CaseDraftDeleted:
    case CaseLogEventType.CaseNoteRemoved:
      return <TmdxChip size="sm">{matchingNote?.message || unosId}</TmdxChip>;
    case CaseLogEventType.CaseStatusUpdated:
      return renderFromTo(
        <TmdxChip size="sm">{getCaseStatusName(record.previousValue as CaseStatus, randomization?.result)}</TmdxChip>,
        <TmdxChip size="sm" color="info">
          {getCaseStatusName(record.value as CaseStatus, randomization?.result)}
        </TmdxChip>,
      );
    case CaseLogEventType.CaseResourceReserved:
      return renderResource(
        record.resource,
        <TmdxChip size="sm" color="info">
          Reserved
        </TmdxChip>,
      );
    case CaseLogEventType.CaseResourceAssigned:
      return renderResource(
        record.user,
        <TmdxChip size="sm" color="info">
          Assigned
        </TmdxChip>,
        record.group && <span className="text-sm">{CaseUserGroupNames[record.group]}</span>,
      );
    case CaseLogEventType.CaseResourceReleased:
      return renderResource(
        record.resource,
        <TmdxChip size="sm" color="danger">
          Released
        </TmdxChip>,
        record.group && <span className="text-sm">{CaseUserGroupNames[record.group]}</span>,
      );
    case CaseLogEventType.CaseResourceUnassigned:
      return renderResource(
        record.user,
        <TmdxChip size="sm" color="danger">
          Unassigned
        </TmdxChip>,
        record.group && <span className="text-sm">{CaseUserGroupNames[record.group]}</span>,
      );
    case CaseLogEventType.CaseResourceInvitationAccepted:
      return (
        <TmdxChip size="sm" color="info">
          {RESOURCE_STATUS_TEXT_MAP[record.value as ResourceLogisticsStatus]}
        </TmdxChip>
      );
    case CaseLogEventType.CaseResourceReassigned:
      return renderFromTo(renderResource(record.previousValue), renderResource(record.value));
    case CaseLogEventType.ResourceStatusUpdated:
      return renderFromTo(
        renderResource(
          record.previousValue,
          <TmdxChip size="sm">Reserved</TmdxChip>,
          record.group && <span className="text-sm">{CaseUserGroupNames[record.group]}</span>,
        ), // TODO: Statuses should be dynamic
        renderResource(
          record.value,
          <TmdxChip size="sm" color="info">
            Assigned
          </TmdxChip>,
          record.group && <span className="text-sm">{CaseUserGroupNames[record.group]}</span>,
        ),
      );
    case CaseLogEventType.DonorORTimeDelayAdded:
      return (
        <TmdxChip size="sm" color="danger">
          {record.value}
        </TmdxChip>
      );
    case CaseLogEventType.DonorORTimeDelayUpdated:
      return renderFromTo(
        <TmdxChip size="sm" color="danger">
          {record.previousValue}
        </TmdxChip>,
        <TmdxChip size="sm" color="danger">
          {record.value}
        </TmdxChip>,
      );
    case CaseLogEventType.InvoiceCreated:
    case CaseLogEventType.AttachFiles:
    case CaseLogEventType.DetachFile:
      return (
        <TmdxChip size="sm" variant="bordered" startContent={<PiFilePdfDuotone size={12} />}>
          {record.value}
        </TmdxChip>
      );
    case CaseLogEventType.ReAllocatedCaseCreated:
    case CaseLogEventType.ReAllocatedCaseUpdated:
      return (
        <TmdxChip size="sm" color="info">
          <LinkToCase id={record.value} />
        </TmdxChip>
      );
    case CaseLogEventType.CaseInitiatedFromAnotherDraft:
      return (
        <TmdxChip size="sm" color="info">
          <LinkToCase id={record.originalCase?.caseId ?? record.value} />
        </TmdxChip>
      );
    case CaseLogEventType.CaseSplitIntoOrgans: {
      const splitCases = (record.splittedCases ?? []).filter((splitCase) => splitCase.caseId !== caseData.caseId);

      return (
        <div className="flex flex-wrap items-center gap-2">
          {splitCases.map((splitCase) => (
            <TmdxChip key={splitCase.caseId} size="sm" color="info">
              <LinkToCase id={splitCase.caseId} />
            </TmdxChip>
          ))}
        </div>
      );
    }
    case CaseLogEventType.RoutePlanningStatusUpdated:
      return (
        <TmdxChip size="sm" color="info">
          {routeStatusNames[record.value as RoutePlanStatus]}
        </TmdxChip>
      );
    case CaseLogEventType.CaseResourceLogisticsStatusUpdated:
      return (
        <div className="flex items-center text-m-r gap-2">
          <div>{caseResourceTypeNames[record.resourceType as CaseResourceType]}</div>
          <TmdxChip size="sm" color="info">
            {RESOURCE_STATUS_TEXT_MAP[record.value as ResourceLogisticsStatus] ||
              resourceLogisticsStatusNames[record.value as ResourceLogisticsStatus]}
          </TmdxChip>
        </div>
      );
    case CaseLogEventType.CaseQuoteDryRunDiscountExcluded:
      return (
        <div className="flex items-center gap-2">
          <span className="text-m-r">Dry Run Discount shall not be a part of the offer for the case.</span>
          <TmdxChip size="sm" color="info">
            System user
          </TmdxChip>
        </div>
      );
    case CaseLogEventType.OrganAcceptanceCriteriaUpdated:
    case CaseLogEventType.OrganAcceptanceCriteriaCleared:
    case CaseLogEventType.OrganAcceptanceCriteriaReset:
    case CaseLogEventType.CaseDetailsUpdated:
    case CaseLogEventType.CaseRecipientHospitalORDataUpdated:
    case CaseLogEventType.CaseQuoteUpdated:
    case CaseLogEventType.CaseLinkAdded:
    case CaseLogEventType.CaseLinkRemoved:
    case CaseLogEventType.CaseLinkUpdated:
    case CaseLogEventType.CaseReviewUpdated:
    case CaseLogEventType.OpoContactsAssigned:
    case CaseLogEventType.OpoContactsUnassigned:
    case CaseLogEventType.RouteCommentUpdated:
    case CaseLogEventType.ServiceCompleted:
    case CaseLogEventType.CaseDataSyncStatusChanged:
    case CaseLogEventType.CaseRouteInitiated:
    case CaseLogEventType.CustomRouteInitiated:
    case CaseLogEventType.QuestionnaireResultSet:
      return renderDetailsRecordField(record);
    case CaseLogEventType.DonorDataUpdated:
      return renderDonorDataUpdatedRecordField(record);
    case CaseLogEventType.ServiceDeclined:
    case CaseLogEventType.CaseDeclined:
      return (
        <TmdxChip size="sm" color="danger">
          {outcomeTypeNames[record.value as OutcomeType]}
        </TmdxChip>
      );
    case CaseLogEventType.LightsSirensSupportChanged:
      return <TmdxChip size="sm">{yesNo(record.value === 'True')}</TmdxChip>;
    case CaseLogEventType.QuestionaryEditAllowanceChanged:
      return <TmdxChip size="sm">{yesNo(record.value === 'True')}</TmdxChip>;
    case CaseLogEventType.CaseAssignedEmployeePrimaryChanged:
      return renderResource(
        record.user,
        null,
        record.group && (
          <div className="flex gap-1">
            <span className="text-sm">
              {CaseUserGroupNames[record.group][CaseUserGroupNames[record.group].length - 1] === 's'
                ? CaseUserGroupNames[record.group].slice(0, -1)
                : CaseUserGroupNames[record.group]}
            </span>
            <span className="text-sm">{record.isPrimary ? 'marked as Primary' : 'no longer marked as Primary'}</span>
          </div>
        ),
      );
    case CaseLogEventType.RandomizationResultSet:
      return (
        <TmdxChip size="sm" color="info">
          {randomizationTypeNames[record.value as RandomizationResultType]}
        </TmdxChip>
      );
    case CaseLogEventType.OrganDeclined: {
      const declineReason = record.declineReason as OrganOnOCSDeclineReason | undefined;

      return (
        declineReason && (
          <TmdxChip size="sm" color="info">
            {ORGAN_DECLINED_TEXTS[declineReason] ?? declineReason}
          </TmdxChip>
        )
      );
    }
    case CaseLogEventType.CaseQuoteApprovedByTxCenter:
    case CaseLogEventType.CaseQuoteRejectedByTxCenter:
    case CaseLogEventType.CaseQuoteRecalled:
      return log.quoteTotals?.totalInCents && log.quoteTotals.currency ? (
        <div className="flex gap-2 items-center">
          <span className="text-s-m text-base-5">Quote amount</span>
          <TmdxChip size="sm">{formatCurrency(log.quoteTotals.totalInCents / 100, log.quoteTotals.currency)}</TmdxChip>
        </div>
      ) : null;
    case CaseLogEventType.CaseSalesOrderUpdated:
      return (
        <TmdxChip size="sm" color="info">
          {caseSalesOrderStatusNames[record.value as CaseSalesOrderStatus]}
        </TmdxChip>
      );
    case CaseLogEventType.IdentityAccessTicketGranted:
    case CaseLogEventType.IdentityAccessTicketRevoked: {
      const logRecord = getIdentityAccessLogRecord(log.eventType, record);

      if (!logRecord) return null;

      return (
        <div className="flex items-center gap-2">
          <span className="text-m-r">{logRecord.textPrefix}</span>
          <TmdxAvatar
            radius="md"
            size="md"
            name={logRecord.target.fullName}
            type="user"
            src={logRecord.target.photoPath}
          />
          <span className="text-m-m">{logRecord.target.fullName}</span>
        </div>
      );
    }
    case CaseLogEventType.CaseResourceLabelsChanged: {
      const isLabelAdded = record.propertyName === 'LabelAdded';
      const logRecordEventName = isLabelAdded ? 'marked as' : 'no longer marked as';
      const employeeLabelEventName = employeeLabelLogEventName[record.value as EmployeeLabels];

      return (
        <div className="flex items-center gap-2">
          <span className="text-m-r">{`Clinical Specialist ${logRecordEventName} ${employeeLabelEventName}`}</span>
          {renderResource(record.resource)}
        </div>
      );
    }

    default:
      return null;
  }
};
